/**
 * Export formats editor — the organization's saved layouts for the flat
 * exports (budget pack Export tab, raw data CSV), with an optional override
 * per location. Opens from the grid's export menu; the preview renders the
 * current version through the same builder the download uses.
 *
 * Only org admins save; everyone else sees the layout in effect, read-only.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, FileSpreadsheet, Plus, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { getExportFormats, saveExportFormat } from '../../api';
import type { ExportFormatsResponse } from '../../api';
import {
  COLUMN_CATALOG, EXPORT_TARGETS, MONTH_HEADER_PRESETS,
  buildFormattedExportRows, glImportFormat, resolveExportFormat, standardFormat,
} from '../../utils/exportFormats';
import type { BudgetExportFormat, ExportColumnKey, ExportFormatTarget } from '../../utils/exportFormats';
import type { BuildBudgetPackInput } from '../../utils/budgetPackExport';

interface ExportFormatsModalProps {
  orgId: number;
  locationId?: number | null;
  locationName?: string;
  initialTarget?: ExportFormatTarget;
  /** Resolves the export input for the preview (stat accounts + chart included). */
  loadPreviewInput: () => Promise<BuildBudgetPackInput>;
  onClose: () => void;
  /** Called with the fresh slots after a save or removal. */
  onSaved?: (formats: ExportFormatsResponse['formats']) => void;
}

type Scope = 'org' | 'location';
const PREVIEW_ROWS = 14;
const clone = (f: BudgetExportFormat): BudgetExportFormat => JSON.parse(JSON.stringify(f));
const SOURCE_LABEL = { location: 'this property’s override', org: 'the organization default', standard: 'the Lodgeic standard layout' } as const;

export function ExportFormatsModal({ orgId, locationId, locationName, initialTarget = 'packExport', loadPreviewInput, onClose, onSaved }: ExportFormatsModalProps) {
  const [data, setData] = useState<ExportFormatsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [target, setTarget] = useState<ExportFormatTarget>(initialTarget);
  const [scope, setScope] = useState<Scope>(locationId ? 'location' : 'org');
  const [draft, setDraft] = useState<BudgetExportFormat>(() => standardFormat(initialTarget));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewInput, setPreviewInput] = useState<BuildBudgetPackInput | null>(null);
  const [customHeader, setCustomHeader] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getExportFormats(orgId, locationId)
      .then((res) => { if (!cancelled) setData(res); })
      .catch((err) => { if (!cancelled) setLoadError(err?.response?.data?.error || 'Could not load export formats'); });
    loadPreviewInput()
      .then((input) => { if (!cancelled) setPreviewInput(input); })
      .catch((err) => console.error('[ExportFormatsModal] preview input failed', err));
    return () => { cancelled = true; };
  }, [orgId, locationId, loadPreviewInput]);

  const slots = data?.formats?.[target];
  const saved = scope === 'org' ? slots?.org ?? null : slots?.location ?? null;
  const effective = resolveExportFormat(slots);
  const canEdit = !!data?.canEdit;

  // Load the slot being edited whenever the target/scope (or the saved data) changes.
  useEffect(() => {
    const seed = saved ?? (scope === 'location' ? slots?.org : null) ?? standardFormat(target);
    setDraft(clone(seed));
    setCustomHeader(!MONTH_HEADER_PRESETS.some((p) => p.value === seed.monthHeader));
    setDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, scope, data]);

  const update = (patch: Partial<BudgetExportFormat>) => { setDraft((d) => ({ ...d, ...patch })); setDirty(true); };
  const updateColumn = (idx: number, patch: Partial<BudgetExportFormat['columns'][number]>) =>
    update({ columns: draft.columns.map((c, i) => (i === idx ? { ...c, ...patch } : c)) });
  const moveColumn = (idx: number, delta: number) => {
    const next = [...draft.columns];
    const [c] = next.splice(idx, 1);
    next.splice(idx + delta, 0, c);
    update({ columns: next });
  };
  const addableKeys = (Object.keys(COLUMN_CATALOG) as ExportColumnKey[])
    .filter((k) => k === 'static' || !draft.columns.some((c) => c.key === k));

  const preview = useMemo(() => {
    if (!previewInput) return null;
    try {
      return buildFormattedExportRows(previewInput, draft);
    } catch (err) {
      console.error('[ExportFormatsModal] preview failed', err);
      return null;
    }
  }, [previewInput, draft]);

  const persist = async (format: BudgetExportFormat | null) => {
    setSaving(true);
    try {
      // locationId rides along on org saves too, so the response carries this property's override slot.
      const res = await saveExportFormat({ orgId, target, scope, locationId, format });
      setData((d) => (d ? { ...d, formats: res.formats } : d));
      onSaved?.(res.formats);
      toast.success(format ? 'Export format saved' : scope === 'location' ? 'Property override removed' : 'Organization default removed');
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Could not save the export format');
    } finally {
      setSaving(false);
    }
  };

  const hasMonths = draft.columns.some((c) => c.key === 'months');
  const scopeName = scope === 'org' ? 'the organization' : (locationName || 'this property');

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !dirty) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start gap-3 px-6 pt-5 pb-3 border-b border-slate-100">
          <div className="p-2 bg-slate-100 rounded-lg flex-shrink-0"><FileSpreadsheet className="w-5 h-5 text-slate-600" /></div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-slate-900">Export formats</h2>
            <p className="text-[12.5px] text-slate-500">
              Layout of the flat exports for {locationName || 'this property'} — currently {SOURCE_LABEL[effective.source]}.
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100" aria-label="Close"><X size={16} /></button>
        </div>

        {/* Target + scope */}
        <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-slate-100">
          <div className="flex rounded-lg bg-slate-100 p-0.5">
            {(Object.keys(EXPORT_TARGETS) as ExportFormatTarget[]).map((t) => (
              <button key={t} onClick={() => setTarget(t)} title={EXPORT_TARGETS[t].description}
                className={`px-3 py-1.5 text-[12.5px] font-medium rounded-md ${target === t ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                {EXPORT_TARGETS[t].label}
              </button>
            ))}
          </div>
          <div className="flex rounded-lg bg-slate-100 p-0.5">
            <button onClick={() => setScope('org')}
              className={`px-3 py-1.5 text-[12.5px] font-medium rounded-md ${scope === 'org' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
              Organization default{slots?.org ? ' •' : ''}
            </button>
            {locationId ? (
              <button onClick={() => setScope('location')}
                className={`px-3 py-1.5 text-[12.5px] font-medium rounded-md ${scope === 'location' ? 'bg-white shadow-sm text-slate-900' : 'text-slate-500 hover:text-slate-700'}`}>
                {locationName || 'This property'} override{slots?.location ? ' •' : ''}
              </button>
            ) : null}
          </div>
          <span className="text-[12px] text-slate-400">
            {saved ? `Saved${saved.updatedAt ? ` ${new Date(saved.updatedAt).toLocaleDateString()}` : ''}${saved.updatedBy ? ` by ${saved.updatedBy}` : ''}` : scope === 'location' ? 'No override — uses the organization default' : 'Not set — uses the Lodgeic standard layout'}
          </span>
        </div>

        {loadError ? (
          <div className="p-6 text-sm text-red-600">{loadError}</div>
        ) : (
          <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-[380px_1fr] overflow-hidden">
            {/* Settings */}
            <fieldset disabled={!canEdit} className="overflow-y-auto px-6 py-4 space-y-5 border-r border-slate-100">
              <Section title="Start from">
                <div className="flex flex-wrap gap-1.5">
                  <PresetButton onClick={() => { const f = standardFormat(target); setDraft(f); setCustomHeader(false); setDirty(true); }}>Lodgeic standard</PresetButton>
                  <PresetButton onClick={() => { const f = glImportFormat(); setDraft(f); setCustomHeader(false); setDirty(true); }}>GL import (account × month #)</PresetButton>
                  {scope === 'location' && slots?.org && (
                    <PresetButton onClick={() => { setDraft(clone(slots.org!)); setDirty(true); }}>Organization default</PresetButton>
                  )}
                </div>
              </Section>

              <Section title="Columns">
                <div className="space-y-1.5">
                  {draft.columns.map((col, idx) => (
                    <div key={`${col.key}-${idx}`} className="flex items-center gap-1.5">
                      <span className="w-[104px] flex-shrink-0 text-[11.5px] text-slate-500 truncate" title={COLUMN_CATALOG[col.key].description}>{COLUMN_CATALOG[col.key].label}</span>
                      {col.key === 'months' ? (
                        <span className="flex-1 text-[12px] text-slate-400 italic">headers below</span>
                      ) : (
                        <input value={col.header ?? COLUMN_CATALOG[col.key].header} onChange={(e) => updateColumn(idx, { header: e.target.value })}
                          className="flex-1 min-w-0 px-2 py-1 text-[12.5px] border border-slate-200 rounded-md" placeholder="Header" />
                      )}
                      {col.key === 'static' && (
                        <input value={col.value ?? ''} onChange={(e) => updateColumn(idx, { value: e.target.value })}
                          className="w-[88px] px-2 py-1 text-[12.5px] border border-slate-200 rounded-md" placeholder="Value" title="Written on every row; {year} expands" />
                      )}
                      <IconBtn disabled={idx === 0} onClick={() => moveColumn(idx, -1)} label="Move up"><ArrowUp size={12} /></IconBtn>
                      <IconBtn disabled={idx === draft.columns.length - 1} onClick={() => moveColumn(idx, 1)} label="Move down"><ArrowDown size={12} /></IconBtn>
                      <IconBtn disabled={col.key === 'months'} onClick={() => update({ columns: draft.columns.filter((_, i) => i !== idx) })} label="Remove"><X size={12} /></IconBtn>
                    </div>
                  ))}
                </div>
                {addableKeys.length > 0 && (
                  <label className="mt-2 flex items-center gap-1.5 text-[12px] text-slate-500">
                    <Plus size={12} />
                    <select value="" onChange={(e) => { const key = e.target.value as ExportColumnKey; if (key) update({ columns: [...draft.columns, { key, ...(key === 'static' ? { value: '' } : {}) }] }); }}
                      className="px-1.5 py-1 text-[12px] border border-slate-200 rounded-md bg-white">
                      <option value="">Add column…</option>
                      {addableKeys.map((k) => <option key={k} value={k}>{COLUMN_CATALOG[k].label}</option>)}
                    </select>
                  </label>
                )}
              </Section>

              <Section title="Month headers">
                <select value={customHeader ? '__custom' : draft.monthHeader}
                  onChange={(e) => { if (e.target.value === '__custom') setCustomHeader(true); else { setCustomHeader(false); update({ monthHeader: e.target.value }); } }}
                  className="w-full px-2 py-1.5 text-[12.5px] border border-slate-200 rounded-md bg-white">
                  {MONTH_HEADER_PRESETS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  <option value="__custom">Custom…</option>
                </select>
                {customHeader && (
                  <>
                    <input value={draft.monthHeader} onChange={(e) => update({ monthHeader: e.target.value })}
                      className="mt-1.5 w-full px-2 py-1 text-[12.5px] font-mono border border-slate-200 rounded-md" />
                    <p className="mt-1 text-[11px] text-slate-400">{'{n} {mm} {mmm} {mmmm} {yyyy} {yy} {kind}'} — a bare {'{n}'} writes numeric headers.</p>
                  </>
                )}
              </Section>

              <Section title="Signs">
                <SignRow label="Expenses" value={draft.expenseSign} onChange={(v) => update({ expenseSign: v })} />
                <SignRow label="Revenue" value={draft.revenueSign} onChange={(v) => update({ revenueSign: v })} />
                <p className="text-[11px] text-slate-400">Positive keeps the grid’s sign; Negative flips it, so a credit (−) line exports positive. Statistics keep their natural sign.</p>
              </Section>

              <Section title="Rows">
                <Toggle checked={draft.statistics} onChange={(v) => update({ statistics: v })} label="Statistics accounts" hint="Room nights, hours and other stat accounts after the money accounts" />
                <Toggle checked={draft.chartZeroFill} onChange={(v) => update({ chartZeroFill: v })} label="Every chart account" hint="Accounts the plan doesn’t carry export as zeros" />
                <Toggle checked={draft.subtotals && draft.sort === 'statement'} disabled={draft.sort !== 'statement'} onChange={(v) => update({ subtotals: v })} label="Subtotal rows" hint="Net Income, section and department totals" />
                <Toggle checked={draft.omitZeroRows} onChange={(v) => update({ omitZeroRows: v })} label="Omit all-zero rows" />
                <label className="flex items-center justify-between gap-2 text-[12.5px] text-slate-700 pt-1">
                  Order
                  <select value={draft.sort} onChange={(e) => update({ sort: e.target.value as BudgetExportFormat['sort'] })}
                    className="px-2 py-1 text-[12.5px] border border-slate-200 rounded-md bg-white">
                    <option value="statement">Statement order</option>
                    <option value="accountNumber">Account number</option>
                  </select>
                </label>
              </Section>

              {target === 'packExport' && (
                <Section title="Workbook">
                  <label className="flex items-center justify-between gap-2 text-[12.5px] text-slate-700">
                    Tab name
                    <input value={draft.sheetName ?? ''} maxLength={31} onChange={(e) => update({ sheetName: e.target.value })} placeholder="Export"
                      className="w-[160px] px-2 py-1 text-[12.5px] border border-slate-200 rounded-md" />
                  </label>
                  <label className="flex items-center justify-between gap-2 text-[12.5px] text-slate-700 mt-1.5">
                    Number format
                    <select value={draft.numberFormat ?? '0.00'} onChange={(e) => update({ numberFormat: e.target.value as BudgetExportFormat['numberFormat'] })}
                      className="w-[160px] px-2 py-1 text-[12.5px] border border-slate-200 rounded-md bg-white">
                      <option value="0.00">1234.56</option>
                      <option value="#,##0.00">1,234.56</option>
                      <option value="0">1235</option>
                      <option value="General">General</option>
                    </select>
                  </label>
                </Section>
              )}

              <Section title="Name">
                <input value={draft.name ?? ''} onChange={(e) => update({ name: e.target.value })} placeholder="e.g. Sage budget import"
                  className="w-full px-2 py-1 text-[12.5px] border border-slate-200 rounded-md" />
              </Section>
            </fieldset>

            {/* Preview */}
            <div className="min-w-0 flex flex-col overflow-hidden px-6 py-4 bg-slate-50/60">
              <div className="flex items-baseline justify-between mb-2">
                <span className="text-[10.5px] font-semibold text-slate-400" style={{ letterSpacing: '0.7px' }}>PREVIEW</span>
                {preview && <span className="text-[11.5px] text-slate-400">{(preview.length - 1).toLocaleString()} rows</span>}
              </div>
              <div className="flex-1 min-h-0 overflow-auto border border-slate-200 rounded-lg bg-white">
                {!preview ? (
                  <div className="p-6 text-[12.5px] text-slate-400">Loading this version’s data…</div>
                ) : (
                  <table className="text-[11.5px] font-mono whitespace-nowrap">
                    <thead className="sticky top-0 bg-slate-100">
                      <tr>{preview[0].map((h, i) => <th key={i} className="px-2 py-1 text-left font-semibold text-slate-600 border-b border-slate-200">{String(h)}</th>)}</tr>
                    </thead>
                    <tbody>
                      {preview.slice(1, PREVIEW_ROWS + 1).map((row, r) => (
                        <tr key={r} className="border-b border-slate-100">
                          {row.map((v, i) => (
                            <td key={i} className={`px-2 py-0.5 ${typeof v === 'number' ? 'text-right tabular-nums' : ''} ${typeof v === 'number' && v < 0 ? 'text-red-600' : 'text-slate-700'}`}>
                              {String(v)}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
              {preview && preview.length - 1 > PREVIEW_ROWS && (
                <p className="mt-1.5 text-[11.5px] text-slate-400">… and {(preview.length - 1 - PREVIEW_ROWS).toLocaleString()} more rows</p>
              )}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-t border-slate-100">
          {!canEdit && data && (
            <span className="text-[12px] text-slate-500">
              {data.configured ? 'Only organization admins can change export formats.' : 'This organization has no configuration document yet.'}
            </span>
          )}
          {canEdit && saved && (
            <button disabled={saving} onClick={() => { void persist(null); }}
              className="text-[12.5px] font-medium text-red-600 hover:underline disabled:opacity-50">
              {scope === 'location' ? 'Remove property override' : 'Remove organization default'}
            </button>
          )}
          <div className="flex-1" />
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-slate-500 hover:bg-slate-100 rounded-lg">
            {canEdit && dirty ? 'Discard' : 'Close'}
          </button>
          {canEdit && (
            <button disabled={saving || !dirty || !hasMonths} onClick={() => { void persist(draft); }}
              className="px-4 py-2 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 text-white text-sm font-medium rounded-lg">
              {saving ? 'Saving…' : `Save for ${scopeName}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-1.5 text-[10.5px] font-semibold text-slate-400" style={{ letterSpacing: '0.7px' }}>{title.toUpperCase()}</div>
      {children}
    </div>
  );
}

function PresetButton({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} className="px-2.5 py-1 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-md hover:bg-slate-50">
      {children}
    </button>
  );
}

function IconBtn({ onClick, disabled, label, children }: { onClick: () => void; disabled?: boolean; label: string; children: React.ReactNode }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label}
      className="p-1 rounded text-slate-400 hover:bg-slate-100 hover:text-slate-600 disabled:opacity-30 disabled:hover:bg-transparent">
      {children}
    </button>
  );
}

function SignRow({ label, value, onChange }: { label: string; value: 'positive' | 'negative'; onChange: (v: 'positive' | 'negative') => void }) {
  return (
    <div className="flex items-center justify-between gap-2 mb-1.5">
      <span className="text-[12.5px] text-slate-700">{label}</span>
      <div className="flex rounded-md border border-slate-200 overflow-hidden">
        {(['positive', 'negative'] as const).map((v) => (
          <button key={v} type="button" onClick={() => onChange(v)}
            className={`px-2.5 py-1 text-[12px] ${value === v ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}>
            {v === 'positive' ? 'Positive' : 'Negative'}
          </button>
        ))}
      </div>
    </div>
  );
}

function Toggle({ checked, onChange, label, hint, disabled }: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string; disabled?: boolean }) {
  return (
    <label className={`flex items-start gap-2 py-1 ${disabled ? 'opacity-50' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} className="mt-0.5" />
      <span>
        <span className="block text-[12.5px] text-slate-700">{label}</span>
        {hint && <span className="block text-[11px] text-slate-400">{hint}</span>}
      </span>
    </label>
  );
}
